import React, { useState, useEffect } from 'react';
import tiktokIcon from '../../assets/icons/tiktok.webp';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link, 
  Divider, 
  Fab,
  List,
  ListItem,
  ListItemIcon,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import { 
  Facebook, 
  Email, 
  Phone, 
  LocationOn, 
  KeyboardArrowUp, 
  ShoppingCart, 
  WhatsApp
} from '@mui/icons-material';

function Footer() {
  const [showButton, setShowButton] = useState(false);
  const whatsappNumber = "17171136"; 
  const facebookUrl = "https://www.facebook.com/bhutandairyproducts/";
  const tiktokUrl = "https://www.tiktok.com/@koufouku.internat";
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.pageYOffset > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleWhatsAppClick = () => {
    const message = "Hello Koufuku, I have a question about your products.";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleTikTokClick = () => {
    window.open(tiktokUrl, '_blank');
  }; 

  const handleFacebookClick = () => {
    window.open(facebookUrl, '_blank');
  };

  const quickLinks = [
    { text: 'About Us', href: '/about' },
    { text: 'Products', href: '/products' },
    { text: 'Kil Gallery', href: '/gallery' },
    { text: 'Contact Us', href: '/contact' },
    { text: 'Distributors', href: '/dealers' },
    { text: 'Offers', href: '/offers' }
  ];

  const contactInfo = [
    { icon: <LocationOn fontSize="small" />, text: 'Chenangri, Trashigang, Bhutan' },
    { icon: <Phone fontSize="small" />, text: '(+975) 17171136' },
    { icon: <Email fontSize="small" />, text: 'koufukucustomercare@gmail.com' }
  ];

  const socialMedia = [
    { 
      icon: <Facebook sx={{ color: '#1877F2' }} />, 
      label: 'Facebook', 
      action: handleFacebookClick,
      bgColor: 'rgba(24, 119, 242, 0.1)',
      hoverColor: 'rgba(24, 119, 242, 0.2)'
    },
    { 
      icon: <WhatsApp sx={{ color: '#25D366' }} />, 
      label: 'WhatsApp', 
      action: handleWhatsAppClick,
      bgColor: 'rgba(37, 211, 102, 0.1)',
      hoverColor: 'rgba(37, 211, 102, 0.2)'
    },
    { 
        icon: <img src={tiktokIcon} alt="TikTok" width="24" height="24" />, 
        action: handleTikTokClick,
        url: 'https://www.tiktok.com/@cpz.enterprise', 
        label: 'TikTok',
      bgColor: 'rgba(37, 211, 102, 0.1)',
      hoverColor: 'rgba(37, 211, 102, 0.2)'
      }
  ];

  return (
    <>
      <Box
        component="footer"
        sx={{
          backgroundColor: '#2c3e50',
          color: '#ecf0f1',
          py: 6,
          mt: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={isMobile ? 2 : 4}>
            {/* Company Info Column - Wider on desktop */}
            <Grid item xs={12} md={5}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'space-between'
              }}>
                <div>
                  <Typography 
                    variant="h5" 
                    component="div" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 2,
                      background: 'linear-gradient(45deg, #e74c3c 30%, #f1c40f 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    KOUFUKU INTERNATIONAL LIMITED (KIL)
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2, 
                    lineHeight: 1.6,
                    pr: isMobile ? 0 : 4
                  }}>
                    Bhutan's premier dairy processor (KIL), established by DHI and SNBL in 2012, now fully owned by DHI since 2020.
                  </Typography>
                </div>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1.5, 
                  mt: 2,
                  flexWrap: 'wrap'
                }}>
                  {socialMedia.map((item, index) => (
                    <Tooltip key={index} title={item.label} arrow>
                      <Link 
                        component="button"
                        onClick={item.action}
                        color="inherit" 
                        aria-label={item.label}
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          backgroundColor: item.bgColor,
                          '&:hover': {
                            backgroundColor: item.hoverColor,
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {item.icon}
                      </Link>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Quick Links Column - Adjusted spacing */}
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: '#fff',
                fontSize: '1.1rem'
              }}>
                Quick Links
              </Typography>
              <List dense disablePadding>
                {quickLinks.map((link, index) => (
                  <ListItem 
                    key={index} 
                    disableGutters 
                    sx={{ 
                      py: 0.5,
                      '&:hover': {
                        transform: 'translateX(4px)'
                      },
                      transition: 'transform 0.2s ease'
                    }}
                  >
                    <Link 
                      href={link.href}
                      color="inherit" 
                      underline="hover"
                      sx={{
                        fontSize: '0.9rem',
                        '&:hover': {
                          color: '#f1c40f',
                        },
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>•</span> {link.text}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Contact Info Column - Better alignment */}
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: '#fff',
                fontSize: '1.1rem'
              }}>
                Contact Info
              </Typography>
              <List dense disablePadding>
                {contactInfo.map((item, index) => (
                  <ListItem 
                    key={index} 
                    disableGutters 
                    sx={{ 
                      py: 1,
                      alignItems: 'flex-start'
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: '32px', 
                      color: '#f1c40f',
                      mt: '2px'
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <Typography variant="body2" sx={{ 
                      lineHeight: 1.4,
                      fontSize: '0.9rem'
                    }}>
                      {item.text}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Map Column - Better proportions */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: '#fff',
                fontSize: '1.1rem'
              }}>
                Our Location
              </Typography>
              <Box sx={{ 
                width: '100%', 
                height: isMobile ? '200px' : '180px',
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease'
              }}>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3955.479809923222!2d91.52919977576704!3d27.32255434235736!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x375c73e6248d6335%3A0x4084b5ae4130080e!2sChenary!5e1!3m2!1sen!2sbt!4v1749526239474!5m2!1sen!2sbt" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Our Location"
                ></iframe>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ 
            my: 4, 
            borderColor: 'rgba(255,255,255,0.1)',
            borderBottomWidth: '1px'
          }} />

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.85rem'
            }}>
              &copy; {new Date().getFullYear()} Koufuku International Limited (KIL). All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Order Now Button - Consistent with design */}
      <Fab
        variant="extended"
        color="primary"
        aria-label="Order Now"
        href="/order"
        sx={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          backgroundColor: '#e74c3c',
          color: 'white',
          boxShadow: 3,
          '&:hover': {
            backgroundColor: '#c0392b',
            transform: 'translateY(-2px)',
            boxShadow: 4
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          transition: 'all 0.2s ease',
          zIndex: 1000,
          px: 3,
          height: 48
        }}
      >
        <ShoppingCart sx={{ mr: 1 }} />
        Order Now
      </Fab>

      {/* Back to Top Button */}
      {showButton && (
        <Fab
          onClick={scrollToTop}
          aria-label="Back to top"
          size="medium"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: '#e74c3c',
            color: 'white',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: '#c0392b',
              transform: 'translateY(-2px)',
              boxShadow: 4
            },
            transition: 'all 0.2s ease',
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      )}
    </>
  );
}

export default Footer;